import { Stack } from "@/Components/v2/atoms/Stack";
import StatusBanner from "./components/StatusBanner";
import { useActionPlanResultsContext } from "./ActionPlanResultsProvider";
import RevenueMetricCards from "./components/RevenueMetricCards";
import { useTheme } from "@mui/material";
import { Box } from "@/Components/v2/atoms/Box";
import { Typography } from "@/Components/v2/atoms/Typography";

import { getTimeSeriesChart } from "./helpers/time-series-chart/getTimeSeriesChart";
import LineChart from "@/Components/v2/molecule/LineChart/LineChart";
import { ABBREVIATED_OPTION, HIDE_DECIMAL, useConfig } from "@/hooks/useConfig";
import { OutlinedButton, LoadingButton } from "@/Components/v2/atoms/Button";
import { useScenariosV3Routes } from "@/pages/scenario-builder/v3/hooks/useScenariosV3Routes";
import { isEmpty } from "lodash";
import Recommendations from "./components/Recommendations";
import { getProgressMetrics } from "./helpers/getProgressMetrics";
import { ProgressCard } from "@/Components/v2/molecule/ProgressCard";
import { ScenarioBanner } from "./components/ScenarioBanner";
import { Download } from "@mui/icons-material";
import { useDownloadSpendingPlan } from "../../api/v2/getSpendingPlan";
import RefreshBanner from "./components/RefreshBanner";

export const ActionPlanResultsPage = () => {
  const theme = useTheme();
  const { actionPlan } = useActionPlanResultsContext();
  const { config } = useConfig();
  const formatCurrency = (value: number) => config.currency.formatCurrency(value, HIDE_DECIMAL);
  const { navigateToScenarioResults } = useScenariosV3Routes();

  const { mutate: downloadSpendingPlan, isPending: isSpendingPlanLoading } =
    useDownloadSpendingPlan({
      actionPlan: actionPlan!,
    });

  if (!actionPlan) {
    return <div>Action plan not found</div>;
  }

  const {
    isActionPlanComplete,
    status,
    revenueTarget,
    currentRevenueDifference,
    liveRevenueForecast,
    liveScenario,
    isActionPlanRefreshed,
    isActionPlanRefreshing,
  } = actionPlan;

  const { timeSeriesChart, axisLabels, tooltipOptions, overrides } = getTimeSeriesChart({
    actionPlan,
    formatCurrency: config.currency.formatCurrency,
    theme,
  });

  const progressMetrics = getProgressMetrics(actionPlan);

  const handleDownloadSpendingPlan = () => {
    downloadSpendingPlan();
  };

  return (
    <Stack gap={3}>
      {isActionPlanRefreshing && <RefreshBanner />}
      {!isActionPlanComplete && (
        <StatusBanner status={status} currentRevenueDifference={currentRevenueDifference} />
      )}
      {progressMetrics.length > 0 && (
        <Stack direction="row" spacing={2}>
          {progressMetrics.map((metric) => (
            <Box key={metric.title} flex={1}>
              <ProgressCard {...metric} isCompleted={isActionPlanComplete} />
            </Box>
          ))}
        </Stack>
      )}
      <Stack
        direction="row"
        border={`1px solid ${theme.palette.divider}`}
        borderRadius={4}
        width="100%"
        overflow="hidden"
      >
        {!isActionPlanComplete && (
          <RevenueMetricCards
            status={status}
            revenueTarget={revenueTarget}
            liveRevenueForecast={liveRevenueForecast}
            currentRevenueDifference={currentRevenueDifference}
          />
        )}
        <Box flexGrow={1} pt={3} pb={2} pl={2} sx={{ minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" pr={2}>
            <Typography fontSize={16} fontWeight={600} sx={{ ml: 2 }}>
              Action Plan Summary
            </Typography>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              {liveScenario?.uuid && (
                <OutlinedButton onClick={() => navigateToScenarioResults(liveScenario!.uuid!)}>
                  View Scenario
                </OutlinedButton>
              )}
              {!isEmpty(timeSeriesChart) && (
                <LoadingButton
                  variant="outlined"
                  startIcon={<Download />}
                  loading={isSpendingPlanLoading}
                  loadingPosition="start"
                  onClick={handleDownloadSpendingPlan}
                >
                  Download Spending Plan
                </LoadingButton>
              )}
            </Stack>
          </Stack>
          {isEmpty(timeSeriesChart) ? (
            <Box
              height="100%"
              width="100%"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Typography variant="body1">No data available</Typography>
            </Box>
          ) : (
            <LineChart
              series={timeSeriesChart}
              axisLabels={axisLabels || []}
              yAxisLabelFormatter={(value) =>
                config.currency.formatCurrency(value, {
                  ...ABBREVIATED_OPTION,
                  minAbbreviatedRange: 1e3,
                })
              }
              tooltipOptions={tooltipOptions}
              overrides={overrides}
            />
          )}
        </Box>
      </Stack>
      {!isActionPlanComplete && (
        <Recommendations actionPlan={actionPlan} formatCurrency={formatCurrency} />
      )}
      {!isActionPlanComplete && isActionPlanRefreshed && <ScenarioBanner actionPlan={actionPlan} />}
    </Stack>
  );
};
